/**
 * transcriptionOutcome — what the patient is told when speech-to-text does not
 * produce text. v242.110.0 (WHISPER-REMOVAL-1).
 *
 * ★ WHAT CHANGED, AND WHY THE OLD APOLOGY IS GONE.
 * This file used to exist because the batch leg answered a failed transcription
 * with an OPERATOR note — "Set OPENAI_API_KEY to enable Whisper transcription."
 * — and three screens rendered that sentence to patients. The helper turned it
 * into something a person could read.
 *
 * That leg is now deleted. OPENAI_API_KEY had been absent from every secret
 * group since D48b, so it could only ever fail; a fallback that cannot succeed
 * is not a fallback, it is a second way to show an error. Amazon Transcribe
 * streaming is the only engine, and when it does not connect there is no second
 * attempt to make on the patient's behalf — they type instead.
 *
 * So the message changed shape. It no longer apologises for a conversion that
 * failed; it says the live connection did not happen, that the recording is
 * safe, and what to do. ORELLA_RULES 4 still applies: whose fault an error
 * describes decides what sentence the user reads, and our transport is never
 * the patient's to read about.
 */

// A WebM/Opus container with a moment of silence in it still weighs a few
// hundred bytes; anything under this carries no speech worth sending.
export const MIN_AUDIO_BYTES = 1200

// recordingTime ticks once per second, so a value below 1 is a tap, not a
// recording. Only ever consulted on the fallback leg — if the stream committed
// text, that text wins and none of this runs.
export const MIN_AUDIO_SECONDS = 1

/**
 * Did the patient actually record anything? Checked BEFORE any upload, so a
 * stray tap costs no round trip, no PHI in flight, and no error banner.
 */
export function isTooShortToTranscribe(blob, seconds = 0) {
  const bytes = (blob && blob.size) || 0
  return bytes < MIN_AUDIO_BYTES || (seconds || 0) < MIN_AUDIO_SECONDS
}

/**
 * The sentence for "the live transcription never connected".
 *
 * Deliberately NOT "I couldn't turn that into text": nothing tried and failed to
 * convert anything. The stream did not open, or dropped. The recording itself is
 * saved either way and the message says so, so there is always a way forward.
 *
 * `err` is kept for the CONSOLE, where whoever is debugging this can read it.
 * It describes our transport, not anything the patient did.
 *
 * `t` is optional ON PURPOSE. The first caller wired up here was a hook with no
 * i18n context, and the resulting ReferenceError would have fired on the exact
 * path this function exists to make safe — a fix that breaks worse than the bug.
 * A helper whose job is "never show the patient something frightening" must not
 * itself throw.
 */
const STREAM_UNAVAILABLE =
  "Live transcription didn't connect — your recording is saved. You can type what you said, or record again."

export function streamUnavailableMessage(err, t, ns) {
  const diagnostic = (err && (err.message || err.name)) || '(no detail)'
  console.warn(`[${ns}] live transcription unavailable —`, diagnostic)
  if (typeof t !== 'function') return STREAM_UNAVAILABLE
  return t(`${ns}.transcriptionUnavailable`, STREAM_UNAVAILABLE)
}

/**
 * ★★★ WHY THE MICROPHONE NEEDED ITS OWN ANSWER — APL-3, v242.172.0.
 *
 * `streamUnavailableMessage` says two things: *your recording is saved*, and *record
 * again*. Both are true when the CAPTURE succeeded and the socket did not, which is the
 * case it was written for. Both are FALSE when `getUserMedia` itself threw — nothing was
 * captured, so there is no recording to be safe, and tapping the mic again re-throws the
 * same denial for as long as the permission stands. `useVoiceInput` routed every failure
 * to that sentence, so the mic on Home and in the chat told a patient their recording was
 * saved when no audio had ever existed. §1dk(e) on an error message: a sentence that
 * looks exactly like a working error message.
 *
 * ★★ AND THERE WERE THREE ANSWERS TO ONE QUESTION. DescribeVoice classified
 * NotAllowedError / PermissionDeniedError / NotFoundError; FollowUpReportModal handled
 * only NotAllowedError, so a patient with NO microphone was told to check their settings
 * rather than that there is no microphone; `useVoiceInput` classified nothing at all.
 * One owner, three readers.
 *
 * ★ `native` decides WHERE the patient is sent, and it matters: "your browser settings"
 *   is useless advice inside a WKWebView, where the switch is Settings → Orella →
 *   Microphone. Callers pass `isNativeApp()`; this file imports nothing, so a gate can
 *   run it in node.
 */
export const MIC_DENIED = 'denied'
export const MIC_NO_DEVICE = 'no-device'
export const MIC_OTHER = 'other'

/** Why did the microphone not open? Names only, because that is all the platform gives. */
export function micFailureKind(err) {
  const name = (err && (err.name || err.code)) || ''
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
    return MIC_DENIED
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
    return MIC_NO_DEVICE
  }
  return MIC_OTHER
}

const MIC_FALLBACK = {
  [MIC_DENIED]: "Orella doesn't have permission to use the microphone, so nothing was recorded. You can turn it on in your device settings, or type what you wanted to say.",
  [MIC_NO_DEVICE]: "No microphone was found, so nothing was recorded. You can type what you wanted to say.",
  [MIC_OTHER]: "The microphone didn't open, so nothing was recorded. You can try again, or type what you wanted to say.",
}

const MIC_KEY = {
  [MIC_DENIED]: 'errors2.micDenied',
  [MIC_NO_DEVICE]: 'errors2.noMicFound',
  [MIC_OTHER]: 'errors2.microphoneAccess',
}

/**
 * What the patient is told when the microphone never opened.
 *
 * ★★ IT NEVER SAYS A RECORDING IS SAFE, because on this path there is no recording. That
 *    single claim is the whole reason this function exists rather than reusing the one
 *    below.
 * ★ `t` is optional for the same reason it is optional there: a helper whose job is to
 *   keep a frightening string off the screen must not itself throw.
 */
export function micFailureMessage(err, t, ns, native = false) {
  const kind = micFailureKind(err)
  const diagnostic = (err && (err.message || err.name)) || '(no detail)'
  console.warn(`[${ns}] microphone unavailable (${kind}) —`, diagnostic)
  const key = kind === MIC_DENIED && native ? 'errors2.micDeniedNative' : MIC_KEY[kind]
  const fallback = kind === MIC_DENIED && native
    ? "Orella doesn't have permission to use the microphone, so nothing was recorded. Open Settings \u2192 Orella \u2192 Microphone to turn it on, or type what you wanted to say."
    : MIC_FALLBACK[kind]
  if (typeof t !== 'function') return fallback
  return t(key, fallback)
}

/**
 * ★★★ WHERE THE PATIENT GOES TO TURN IT BACK ON — APL-3, v242.177.0.
 *
 * `micFailureMessage` was made native-aware in v242.172.0 and a SECOND sentence about the
 * same denial was left behind one screen over. `DescribeVoice` renders
 * `describe.micPermissionHelp` under the error whenever the kind is a denial, and that
 * string reads, in all ELEVEN locales: *On iOS: Settings -> Safari -> Microphone.*
 *
 * ★★ INSIDE THE NATIVE APP THAT IS ADVICE THAT CANNOT WORK. There is no Safari in a
 *    WKWebView build; the switch is Settings -> Orella -> Microphone, and a patient who
 *    follows the sentence lands in the settings for a different app, finds a microphone
 *    toggle there, turns it on, comes back and is still denied. §1dk(e): the failure mode
 *    of this string is a legal-looking value, so it could stay wrong forever.
 *
 * ★★ AND IT IS THE SAME QUESTION `micFailureMessage` ALREADY OWNS, asked a second time by
 *    a second reader with its own answer. That is the shape v242.172.0 was built to end —
 *    one owner, and the callers pass `isNativeApp()`. So it moves here rather than being
 *    corrected in place.
 *
 * ★ The old `describe.micPermissionHelp` key is RETIRED from all eleven locales in the
 *   same version. A key with no reader is dead weight from a key path that moved, which is
 *   exactly what ARM 3 of check-i18n-keys calls an orphan.
 */
const MIC_HELP_NATIVE = 'Open Settings \u2192 Orella \u2192 Microphone to turn it back on.'
const MIC_HELP_WEB = "Allow the microphone in your browser's site settings, then try again."

export function micPermissionHelp(t, native = false) {
  const key = native ? 'errors2.micHelpNative' : 'errors2.micHelpWeb'
  const fallback = native ? MIC_HELP_NATIVE : MIC_HELP_WEB
  if (typeof t !== 'function') return fallback
  return t(key, fallback)
}

/**
 * The sentence for "you did not say anything yet". Deliberately NOT an error:
 * nothing failed, and telling someone their tap was a failure is how a UI
 * teaches people to distrust it.
 */
const NOTHING_RECORDED =
  "I didn't catch anything that time — tap the mic and speak when you're ready."

export function nothingRecordedMessage(t, ns) {
  if (typeof t !== 'function') return NOTHING_RECORDED
  return t(`${ns}.didntCatchThat`, NOTHING_RECORDED)
}
